import { Box, Grid, ResponsiveContext } from "grommet"
import SidebarSection from "../../../layout/SidebarSection"
import RouletteItemCard from "../../strip/RouletteItemCard"
import { Checkbox, Gallery } from "grommet-icons"
import { FontOutline } from "../../../../icons/FontOutline"
import RouletteItemBackgroundSettings from "../RouletteItemBackgroundSettings"
import RouletteItemFrameSettings from "../RouletteItemFrameSettings"
import RouletteItemTextSettings from "../RouletteItemTextSettings"
import { createContext, memo, useContext, useEffect, useMemo, useState } from "react"
import { ResponsiveGrid } from "../../../ResponsiveGrid"
import { rouletteItemBgType, rouletteItemFrameType } from "../../RouletteConstants"
import { useTranslation } from "react-i18next"

const navs = [
    { label: 'roulette:backgroundTitle', icon: <Gallery/>, component: <RouletteItemBackgroundSettings /> },
    { label: 'roulette:borderTitle', icon: <Checkbox/>, component: <RouletteItemFrameSettings /> },
    { label: 'roulette:textTitle', icon: <FontOutline/>, component: <RouletteItemTextSettings /> },
]

const columns = {
    xsmall: ["auto"],
    small: ["auto"],
    medium: ["auto"],
    large: ["flex", "auto"],
    xlarge: ["flex", "auto"]
};

const rows = {
    xsmall: ["flex", "flex"],
    small: ["flex", "flex"],
    medium: ["flex"],
    large: ["flex"],
    xlarge: ["flex"]
};

const areas = {
    xsmall: [
        ['cardPreviewArea'],
        ['sidebarArea']
    ],
    small: [
        ['cardPreviewArea'],
        ['sidebarArea']
    ],
    medium: [
        ['cardPreviewArea'],
        ['sidebarArea']
    ],
    large: [
        ['sidebarArea', 'cardPreviewArea']
    ],
    xlarge: [
        ['sidebarArea', 'cardPreviewArea']
    ]
};

const defaultCardStyle = {
    size: 160,
    radius: 16,
    color: {
        color1: 'rgb(100, 100, 100)',
        color2: 'rgb(100, 100, 100)',
        angle: 180
    },
    shadow: {
        color: 'rgb(0, 0, 0)',
        horizontalOffset: 4,
        verticalOffset: 4,
        blur: 4
    },
    // image: {
    //     url: `${process.env.PUBLIC_URL}/images/bg_cs2_1.webp`,
    //     hueRotate: 0,
    //     saturate: 100
    // }
}

const defaultBorderStyle = {
    size: 170,
    radius: 16,
    width: 6,
    outline: {
        color1: "rgb(165, 0, 92)",
        color2: "rgb(165, 0, 92)",
        gradientAngle: 180
    },
    // image: {
    //     url: '',
    //     hueRotate: 0,
    //     saturate: 100
    // },
    shadow: {
        color: 'rgb(0, 0, 0)',
        horizontalOffset: 0,
        verticalOffset: 0,
        blur: 0
    }
}

const defaultTextStyle = {
    color: 'rgb(255, 255, 255)',
    fontWeight: 'bold',
    fontStyle: 'none',
    textDecoration: 'unset',
    textAlign: 'center',
    textTransform: 'uppercase',
    fontSize: 19,
    stroke: {
        width: 1,
        color: 'rgb(0, 0, 0)'
    },
    shadow: {
        color: 'rgb(0, 0, 0)',
        horizontalOffset: 1,
        verticalOffset: 1,
        blur: 2
    },
    lineHeight: 1.66,
    letterSpacing: 0.55,
    wordSpacing: 0,
    transform: {
        x: 0,
        y: 0
    },
    fontBlockWidth: 160,
    textOutlineBackgroundSize: 0,
    textOutlineBackgroundColor: 'rgba(255, 255, 255, 1)'
}

/*
const defaultBackgroundStyle = {
    radius: 16,
    color: {
        color1: "rgb(63, 63, 63)",
        color2: "rgb(63, 63, 63)",
        angle: 360
    },
    // image: {
    //     url: `${process.env.PUBLIC_URL}/images/bg_cs2_1.webp`,
    //     hueRotate: 0,
    //     saturate: 100
    // },
    shadow: {
        color: 'rgb(0, 0, 0)',
        horizontalOffset: 5,
        verticalOffset: 5,
        blur: 5
    }
}
*/

const getBackgroundStyle = (backgroundStyle) => {
    
    let values = {};

    if (backgroundStyle.color) {
        const color = backgroundStyle.color;
        values = { ...values, ...{
            backgroundImage: `linear-gradient(${color.angle}deg, ${color.color1} 0%, ${color.color2} 100%)`
        }};
    }

    if (backgroundStyle.image) {
        const image = backgroundStyle.image;
        values = { ...values, ...{
            backgroundImage: `url('${image.url}')`,
            filter: `hue-rotate(${image.hueRotate}deg) saturate(${image.saturate}%)`,
        }};
    }

    return values;
}

export const FormTypeContext = createContext();

const RouletteItemDesignTab = ({ designValues, backgroudDesignValues }) => {

    const { t } = useTranslation('roulette');

    const [cardStyle, setCardStyle] = useState(defaultCardStyle);
    const [borderStyle, setBorderStyle] = useState(defaultBorderStyle);
    const [textStyle, setTextStyle] = useState(defaultTextStyle);

    const [backgroundPreviewStyle, setBackgroundPreviewStyle] = useState(getBackgroundStyle(backgroudDesignValues));

    useEffect(() => {
        setBackgroundPreviewStyle(getBackgroundStyle(backgroudDesignValues));
    }, [backgroudDesignValues])

    useEffect(() => {
        let cardStyle = {
            size: designValues.backgroundSize,
            radius: designValues.backgroundRadius
        }

        if (designValues.backgroundType === rouletteItemBgType.COLOR) {
            cardStyle = { ...cardStyle, ...{
                color: {
                    color1: designValues.backgroundColor1,
                    color2: designValues.backgroundColor2,
                    angle: designValues.colorGradientAngle
                },
                shadow: {
                    color: designValues.backgroundShadowColor,
                    horizontalOffset: designValues.backgroundShadowHorizontalOffset,
                    verticalOffset: designValues.backgroundShadowVerticalOffset,
                    blur: designValues.backgroundShadowBlur
                }
            }}
        }

        if (designValues.backgroundType === rouletteItemBgType.IMAGE) {
            cardStyle = { ...cardStyle, ...{
                image: {
                    url: designValues.backgroundImage?.url,
                    hueRotate: designValues.backgroundImageHue,
                    saturate: designValues.backgroundImageSaturation
                },
                shadow: {
                    color: designValues.backgroundShadowColor,
                    horizontalOffset: designValues.backgroundShadowHorizontalOffset,
                    verticalOffset: designValues.backgroundShadowVerticalOffset,
                    blur: designValues.backgroundShadowBlur
                }
            }}
        }
        
        setCardStyle(cardStyle);

        let borderStyle = {
            size: designValues.frameSize,
            radius: designValues.frameRadius,
            width: designValues.frameOutlineWidth,
        }

        if (designValues.frameType === rouletteItemFrameType.OUTLINE) {
            borderStyle = { ...borderStyle, ...{
                outline: {
                    color1: designValues.frameColor1,
                    color2: designValues.frameColor2,
                    gradientAngle: designValues.frameColorGradientAngle,
                },
                shadow: {
                    color: designValues.frameShadowColor,
                    horizontalOffset: designValues.frameShadowHorizontalOffset,
                    verticalOffset: designValues.frameShadowVerticalOffset,
                    blur: designValues.frameShadowBlur
                },
            }}
        }

        if (designValues.frameType === rouletteItemFrameType.IMAGE) {
            borderStyle = { ...borderStyle, ...{
                image: {
                    url: designValues.frameImage?.url,
                    hueRotate: designValues.frameImageHue,
                    saturate: designValues.frameImageSaturation
                },
                shadow: {
                    color: designValues.frameShadowColor,
                    horizontalOffset: designValues.frameShadowHorizontalOffset,
                    verticalOffset: designValues.frameShadowVerticalOffset,
                    blur: designValues.frameShadowBlur
                },
            }}
        }

        setBorderStyle(borderStyle);

        setTextStyle({
            font: designValues.font?.name,
            color: designValues.textColor,
            fontWeight: designValues.textStyle[0].selected ? designValues.textStyle[0].value : 'normal',
            fontStyle: designValues.textStyle[1].selected ? designValues.textStyle[1].value : 'normal',
            textDecoration: designValues.textStyle[2].selected ? designValues.textStyle[2].value : 'none',
            textAlign: designValues.textAlignment,
            textTransform: designValues.textTransformation,
            fontSize: designValues.fontSize,
            stroke: {
                width: designValues.fontOutlineWidth,
                color: designValues.textOutlineColor
            },
            shadow: {
                color: designValues.textShadowColor,
                horizontalOffset: designValues.textShadowHorizontalOffset,
                verticalOffset: designValues.textShadowVerticalOffset,
                blur: designValues.textShadowBlur
            },
            lineHeight: designValues.lineHeight,
            letterSpacing: designValues.letterSpacing,
            wordSpacing: designValues.wordSpacing,
            transform: {
                x: designValues.fontPositionX,
                y: designValues.fontPositionY
            },
            fontBlockWidth: designValues.fontBlockWidth,
            textOutlineBackgroundSize: designValues.textOutlineBackgroundSize,
            textOutlineBackgroundColor: designValues.textOutlineBackgroundColor
        })
    }, [designValues])

    const item = useMemo(() => ({ id: "_777", name: 'Visual Check Перевірка Виду' }), []);

    return (
        <ResponsiveGrid overrideColumns={columns} overrideRows={rows} areas={areas} gap="medium" align="start" justify="center">
            <FormTypeContext.Provider value={{ backgroundType: designValues.backgroundType, frameType: designValues.frameType }}>
                <SidebarSection navs={navs} fill gridArea="sidebarArea"/>
            </FormTypeContext.Provider>
            <Box wrap width={'fit-content'} margin={{ top: 'medium' }} pad={'medium'} background={'background-contrast'} round="small" gridArea="cardPreviewArea">
                <Box height={'200px'} width={'200px'} style={{ position: 'relative', minHeight: '200px', minWidth: '200px' }} justify="center" align="center" fill={false}>
                    <Box style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, minHeight: '200px', minWidth: '200px', ...backgroundPreviewStyle }}></Box>
                    <RouletteItemCard item={item} cardStyle={cardStyle} borderStyle={borderStyle} textStyle={textStyle} />
                </Box>
            </Box>
        </ResponsiveGrid>
    )
}

export default memo(RouletteItemDesignTab);